import { request } from "../../utils/request";
import { systemRequest } from "../../utils/systemRequest";

/**
 * 获取所有通知模板
 * @returns Promise<NOTICE.NoticeTemplatesResponse> 返回通知模板列表
 */
export const getNoticeTemplatesApi = () => {
  return systemRequest<NOTICE.NoticeTemplatesResponse>("/notice/templates", {
    method: "GET"
  });
};

/**
 * 获取当前登录用户未读通知数量
 *
 * 后端接口：/admin-api/video/notify/unread-count
 * 请求方式：GET
 * 响应数据：{ code: 0, data: number, msg: "" }
 */
export const getUnreadNotifyCountApi = () => {
  return request<NOTICE.UnreadCountResponse>("/video/notify/unread-count", {
    method: "GET",
  });
};

/**
 * 获取当前用户的通知列表（新接口）
 *
 * 后端接口：/admin-api/video/notify/page
 * 请求方式：GET
 * 查询参数：pageNo, pageSize
 *
 * 说明：
 * - 为了保持前端其余代码兼容，仍然返回旧的 NOTICE.NoticeListResponse 结构
 * - 在此函数内部对新接口返回的数据进行适配与转换
 */
export const getNoticeListApi = async (
  params: NOTICE.NoticeListParams = {}
): Promise<NOTICE.NoticeListResponse> => {
  const { page = 1, page_size = 10 } = params;

  // 调用新接口（前缀 /admin-api 已在 request 中统一处理）
  const res = await request<NOTICE.VideoNotifyPageResponse>("/video/notify/page", {
    method: "GET",
    params: {
      pageNo: page,
      pageSize: page_size,
    },
  });

  const list = res?.data?.list || [];

  // 将新结构适配为旧的 NoticeItem 结构，便于现有 UI 复用
  const notices: NOTICE.NoticeItem[] = list.map((item) => {
    const readStatus = item.readStatus ? "1" : "0";

    // templateType 模板类型：
    // 1: 业务提醒（类似旧的 notice_type = "2"）
    // 2: 审核请求（类似旧的 notice_type = "3"）
    // 3: 系统公告（类似旧的 notice_type = "2"）
    // processStatus 处理状态：
    // 0: 待处理, 1: 已同意, 2: 已拒绝, 3: 无需处理
    const notice_type =
      item.templateType === 2
        ? "3" // 审核请求，需要显示审核按钮
        : "2"; // 业务提醒或系统公告，显示已读按钮

    return {
      id: item.id,
      user_id: 0, // 新接口没有 userId 字段，这里占位为 0
      notice: {
        notice_id: item.id,
        notice_title: item.templateCode,
        notice_content: item.content,
        notice_type,
        notice_cmd: "",
        create_by: "", // 后端未返回创建者，这里留空
        create_time: item.createTime,
      },
      notice_object: item.params || {},
      read_status: readStatus,
      process_status:
        item.processStatus === null || item.processStatus === undefined
          ? "0"
          : String(item.processStatus),
      create_time: item.createTime,
      read_time: item.readTime || "",
      process_time: item.processTime || "",
    };
  });

  const total = res?.data?.total ?? notices.length;
  const unread = notices.filter((n) => n.read_status === "0").length;

  return {
    total,
    unread,
    notices,
  };
};

/**
 * 处理消息通知
 * @param params 处理参数
 * @param params.user_notice_id 用户通知ID
 * @param params.action 处理动作：approve(同意)、reject(拒绝)、read(已读)
 * @returns Promise<NOTICE.ProcessResponse> 返回处理结果
 */
export const processNoticeApi = (params: NOTICE.ProcessParams) => {
  return systemRequest<NOTICE.ProcessResponse>("/notice/process", {
    method: "POST",
    body: params
  });
};


/**
 * 删除通知
 * 
 * 后端接口：/admin-api/video/notify/delete
 * 请求方式：DELETE
 * 请求参数：id（通知ID）
 * 响应数据：{ code: 0, data: true, msg: "" }
 */
export const deleteNoticeApi = (id: number) => {
  return request<NOTICE.DeleteNoticeResponse>("/video/notify/delete", {
    method: "DELETE",
    params: { id },
  });
};

/**
 * 标记通知为已读
 *
 * 后端接口：/admin-api/video/notify/read
 * 请求方式：PUT
 * 查询参数：id（通知ID）
 */
export const markNoticeAsReadApi = (params: NOTICE.MarkReadParams) => {
  const { id } = params;
  return request<NOTICE.MarkReadResponse>("/video/notify/read", {
    method: "PUT",
    params: { id },
  });
};

/**
 * 标记审核通过 / 不通过
 *
 * 后端接口：/admin-api/video/notify/approve
 * 请求方式：PUT
 * 请求体参数：
 * {
 *   "id": 通知ID,
 *   "approve": true | false
 * }
 */
export const approveNoticeApi = (params: NOTICE.ApproveNoticeParams) => {
  return request<NOTICE.ApproveNoticeResponse>("/video/notify/approve", {
    method: "PUT",
    body: params,
  });
};